// native
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { Observable, forkJoin, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, map, switchMap } from 'rxjs/operators';

// addon
import { TranslateService } from '@ngx-translate/core';

// service
import { DoctorsService } from 'app/core/services/doctors.service';
import { PatientsService } from 'app/core/services/patients.service';
import { PreferencesService } from 'app/core/services/preferences.service';
import { UtilityService } from 'app/core/services/utility.service';
import { ErrorService } from 'app/core/services/error.service';
import { AuthService } from 'app/core/services/auth.service';
import { DialogService } from 'app/core/services/dialog.service';

// models
import { Doctor, PaginatedItems, Patient, PatientRequest, UserPreferences } from '../../../models';
import { PatientMode, PatientStepResult } from '../../home-management.types';

// constants
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from '../../../constants';

@Component({
  selector: 'app-home-patient-step',
  templateUrl: './patient-step.component.html',
  standalone: false
})
export class PatientStepComponent implements OnInit {
  @Input() initial: PatientStepResult | null = null;
  @Output() next: EventEmitter<PatientStepResult> = new EventEmitter<PatientStepResult>();

  mode: PatientMode = 'existing';

  // assign existing
  patientControl = new UntypedFormControl(null);
  patients: Patient[] = [];
  selectedPatient: Patient | null = null;
  isSearching = false;

  // create new
  form: UntypedFormGroup;
  doctors: Doctor[] = [];
  preferences: UserPreferences;

  maxDobDate = new Date();
  maxYearOfBirth = new Date().getFullYear();
  isLoading = false;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private patientsService: PatientsService,
    private doctorsService: DoctorsService,
    public preferencesService: PreferencesService,
    private utilityService: UtilityService,
    private errorService: ErrorService,
    public authService: AuthService,
    private dialogService: DialogService,
    private translateService: TranslateService
  ) { }

  ngOnInit(): void {
    this.isLoading = true;
    forkJoin([
      this.doctorsService.getAll(),
      this.preferencesService.get()
    ]).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe(([doctors, preferences]) => {
      this.doctors = doctors;
      this.preferences = preferences;
      this.initializeForm();
      this.initializePatientSearch();
      this.loadInitialPatients();
      this.restore();
    }, error => this.errorService.handleError(error));
  }

  private restore(): void {
    if (!this.initial)
      return;

    // Older persisted state predates the mode toggle and only captured the
    // create-new form, so default to 'new' when no mode is present.
    this.mode = this.initial.mode ?? 'new';

    if (this.mode === 'existing' && this.initial.existingPatient) {
      this.selectedPatient = this.initial.existingPatient;
      this.patientControl.setValue(this.getPatientName(this.initial.existingPatient), { emitEvent: false });
    }
  }

  private initializeForm(): void {
    const value = this.initial?.formValue;

    this.form = this.formBuilder.group({
      first_name: [value?.first_name ?? null, this.preferences.disable_pi ? null : [Validators.required]],
      last_name: [value?.last_name ?? null, this.preferences.disable_pi ? null : [Validators.required]],
      date_of_birth: [this.restoreDateOfBirth(value?.date_of_birth), Validators.required],
      doctor: [value?.doctor ?? null, Validators.required],
      patient_id_number: [value?.patient_id_number ?? null, this.preferences.disable_pi ? [Validators.required] : null],
      email: [value?.email ?? null, Validators.compose([Validators.email])],
      phone: [value?.phone ?? null]
    });
  }

  // Wizard state is persisted as JSON, so a restored datepicker value comes
  // back as a string; convert it to a Date the datepicker can render.
  private restoreDateOfBirth(value: any): any {
    if (value && typeof value === 'string' && !this.preferences.disable_pi)
      return new Date(value);
    return value ?? null;
  }

  setMode(mode: PatientMode): void {
    this.mode = mode;
  }

  // --- Assign existing patient ---------------------------------------------

  private initializePatientSearch(): void {
    this.patientControl.valueChanges.pipe(
      debounceTime(250),
      distinctUntilChanged(),
      switchMap((term: string) => {
        // Drop a previous selection once the typed text no longer matches it.
        if (this.selectedPatient && this.getPatientName(this.selectedPatient) !== term)
          this.selectedPatient = null;

        this.isSearching = true;
        return this.searchPatients(term).pipe(finalize(() => this.isSearching = false));
      })
    ).subscribe(patients => this.patients = patients, error => this.errorService.handleError(error));
  }

  private loadInitialPatients(): void {
    this.searchPatients(null).subscribe(
      patients => this.patients = patients,
      error => this.errorService.handleError(error)
    );
  }

  private searchPatients(term: string): Observable<Patient[]> {
    const search = (term || '').trim();

    // Encrypted (zero-PII) stores are searched locally; the server only matches
    // on plaintext, which it doesn't have.
    if (this.preferencesService.isZeroPiiEnabled && search) {
      if (search.length < 3)
        return of([]);
      return this.patientsService.searchPatientStore(search).pipe(map(res => res?.results ?? []));
    }

    return this.patientsService.getAll(DEFAULT_PAGE_SIZE, DEFAULT_PAGE_INDEX, search || null, false).pipe(
      map((res: PaginatedItems<Patient>) => res?.results ?? [])
    );
  }

  onSelectExisting(patient: Patient): void {
    this.selectedPatient = patient;
  }

  // Name only (no demographics), used as the autocomplete input value once a
  // patient is picked. Follows the same name rules as the patients list.
  getPatientName(patient: Patient): string {
    return patient ? this.patientsService.getDisplayName(patient) : '';
  }

  // Builds "<name> (<DOB or age>)" consistent with the patients list screen:
  // the name follows getDisplayName (patient id number vs full name), and the
  // demographic follows the same rules as the list's second column - date of
  // birth when showPatientDob is set, otherwise age, and hidden entirely for
  // clinical-trial read-only no-PII users.
  getPatientLabel(patient: Patient): string {
    if (!patient)
      return '';

    const name = this.patientsService.getDisplayName(patient);

    if (this.authService.isClinicalTrialReadOnlyNoPii)
      return name;

    const demographic = this.preferencesService.showPatientDob
      ? this.formatDateOfBirth(patient.date_of_birth)
      : this.formatAge(patient);

    return demographic ? `${name} (${demographic})` : name;
  }

  private formatDateOfBirth(dob: string): string {
    if (this.utilityService.isStringValidDate(dob))
      return new DatePipe('en-US').transform(dob, this.preferencesService.defaultDateFormat.value) || '';
    return dob || '';
  }

  private formatAge(patient: Patient): string {
    return patient.age ? `${patient.age} ${this.translateService.instant('years')}` : '';
  }

  // Translation key for the review-step name row: a patient ID number when no
  // name is shown (PII disabled / clinical-trial read-only no-PII), else 'name'.
  private get nameLabel(): string {
    return this.preferencesService.disablePi || this.authService.isClinicalTrialReadOnlyNoPii
      ? 'patientIdNumber'
      : 'name';
  }

  // Builds the review-step demographic row (label key + value), matching the
  // patients list column. Empty when demographics are hidden.
  private buildDemographic(dob: string, age?: number): { label: string; value: string } {
    if (this.authService.isClinicalTrialReadOnlyNoPii)
      return { label: '', value: '' };

    // Year-of-birth mode stores only a year, so show that rather than a full date.
    if (this.preferencesService.disablePi)
      return { label: 'yearOfBirth', value: this.utilityService.convertServerDateToClientYear(dob) };

    if (this.preferencesService.showPatientDob)
      return { label: 'dateOfBirth', value: this.formatDateOfBirth(dob) };

    // Age display mode: existing patients carry a server-computed age, but a
    // freshly entered patient has none yet, so fall back to the date of birth.
    if (age)
      return { label: 'age', value: `${age} ${this.translateService.instant('years')}` };

    return { label: 'dateOfBirth', value: this.formatDateOfBirth(dob) };
  }

  // --- Step navigation ------------------------------------------------------

  get canProceed(): boolean {
    if (this.mode === 'existing')
      return !!this.selectedPatient;
    return !!this.form && this.form.valid;
  }

  async onNext(): Promise<void> {
    if (this.mode === 'existing') {
      if (this.selectedPatient)
        this.emitExisting(this.selectedPatient);
      return;
    }

    if (!this.form || !this.form.valid)
      return;

    const request = this.buildRequest();

    // Mirror PatientFormComponent: warn on a likely duplicate so staff don't
    // silently create a second record for the same patient. TechAll users skip
    // the check, same as the standard form.
    if (!this.authService.isTechallUserOrAdmin) {
      this.isLoading = true;
      let duplicate: Patient = null;
      try {
        duplicate = await this.patientsService.getDuplicate(request);
      } catch (error) {
        this.errorService.handleError(error?.error);
      } finally {
        this.isLoading = false;
      }

      if (duplicate) {
        this.dialogService.openConfirm({
          action: this.translateService.instant('create'),
          message: this.translateService.instant('patientExistsMessage'),
          confirmText: this.translateService.instant('useExistingPatient'),
          cancelText: this.translateService.instant('createNewPatient'),
          mediumButtonMode: true
        }).then(result => {
          // "Use existing" -> adopt the existing patient and continue to step 2.
          if (result.confirmed)
            this.emitNew(request, duplicate);
          // "Create new" -> proceed creating a new patient on submit.
          else if (result.canceled)
            this.emitNew(request, null);
        });
        return;
      }
    }

    this.emitNew(request, null);
  }

  private buildRequest(): PatientRequest {
    const v = this.form.value;
    const date_of_birth = this.preferencesService.disablePi
      ? `${v.date_of_birth}-01-01`
      : this.utilityService.convertClientDateToServerDate(v.date_of_birth);

    return {
      first_name: v.first_name,
      last_name: v.last_name,
      email: v.email,
      date_of_birth,
      doctor_id: v.doctor,
      patient_id_number: v.patient_id_number,
      phone: v.phone || null
    };
  }

  private emitNew(request: PatientRequest, existingPatient: Patient | null): void {
    const v = this.form.value;
    const doctor = this.doctors.find(d => d.id === v.doctor);

    // Build the demographic from a server-style date string so it formats the
    // same as the patients list. disable_pi only captures a year of birth.
    const dob = this.preferencesService.disablePi ? `${v.date_of_birth}-01-01` : request.date_of_birth;
    const demographic = this.buildDemographic(dob);

    this.next.emit({
      mode: 'new',
      formValue: v,
      request,
      existingPatient,
      summary: {
        name: this.preferences.disable_pi ? (v.patient_id_number || '') : `${v.first_name} ${v.last_name}`,
        nameLabel: this.nameLabel,
        demographic: demographic.value,
        demographicLabel: demographic.label,
        doctorName: doctor ? `${doctor.first_name} ${doctor.last_name}` : ''
      }
    });
  }

  private emitExisting(patient: Patient): void {
    const doctor = this.doctors.find(d => d.id === patient.doctor_id);
    const demographic = this.buildDemographic(patient.date_of_birth, patient.age);

    this.next.emit({
      mode: 'existing',
      formValue: null,
      request: null,
      existingPatient: patient,
      summary: {
        name: this.patientsService.getDisplayName(patient),
        nameLabel: this.nameLabel,
        demographic: demographic.value,
        demographicLabel: demographic.label,
        doctorName: doctor ? `${doctor.first_name} ${doctor.last_name}` : ''
      }
    });
  }
}
